import React, { useState, useEffect } from 'react';
import { getEmployeeTimeEntries, getHolidays, updateTimeEntry, handleTimeChangeRequest } from '../../services/adminService';
import { showError, showConfirm, showToast } from '../../utils/sweetAlert';
import Swal from 'sweetalert2';
import './AttendanceModal.css';

const getTimeString = (dateStr, defaultTime) => {
    if (!dateStr) return defaultTime;
    const d = new Date(dateStr);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

const combineDateAndTimeStr = (dateStr, timeStr) => {
    if (!timeStr) return null;
    return `${dateStr}T${timeStr}`;
};

const AttendanceModal = ({ employee, onClose, onUpdate, month, setMonth, year, setYear }) => {
    const [attendanceData, setAttendanceData] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingRow, setEditingRow] = useState(null);
    const [editData, setEditData] = useState({ loginTime: '', logoutTime: '' });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [month, year]);

    const fetchData = async () => {
        try {
            setLoading(true);
            // Fetch Time Entries
            const entries = await getEmployeeTimeEntries(employee._id, month, year);

            // Fetch Holidays
            const holidayList = await getHolidays();
            setHolidays(holidayList);

            setAttendanceData(entries);
        } catch (error) {
            console.error("Failed to fetch attendance data", error);
        } finally {
            setLoading(false);
        }
    };

    const getDaysInMonth = (m, y) => {
        return new Date(y, m, 0).getDate();
    };

    const generateCalendarRows = () => {
        const daysInMonth = getDaysInMonth(month, year);
        const rows = [];

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month - 1, day);
            const dayOfWeek = date.getDay(); // 0 = Sunday

            // Format date for display YYYY-MM-DD for simpler comparison/display
            const displayDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

            // Find entry for this day
            // entries usually have 'date' field. We need to match it.
            // API returns ISO strings usually.
            const entry = attendanceData.find(e => {
                const eDate = new Date(e.date);
                return eDate.getDate() === day && eDate.getMonth() === (month - 1) && eDate.getFullYear() === year;
            });

            // Check Holiday
            const holiday = holidays.find(h => {
                const hDate = new Date(h.date);
                return hDate.getDate() === day && hDate.getMonth() === (month - 1) && hDate.getFullYear() === year;
            });

            // Future Date Check
            const now = new Date();
            now.setHours(0, 0, 0, 0); // Normalize today
            const currentRowDate = new Date(year, month - 1, day);
            const isFuture = currentRowDate > now;

            // Determine Main Status Text (Time or Absent or Empty)
            // If entry exists -> Time
            // If future -> '-'
            // If Sunday/Holiday -> '' (Empty, unless we want to say "Off")
            // Else -> 'Absent' (Red)

            let timeInText = 'Absent';
            let timeOutText = 'Absent';
            let isAbsent = false;

            if (entry) {
                timeInText = formatTime(entry.loginTime);

                if (entry.logoutTime) {
                    timeOutText = formatTime(entry.logoutTime);
                } else if (!isFuture && currentRowDate < now) {
                    // Past entry with no logout -> Show Auto-Clockout time
                    timeOutText = '11:59 PM (Auto)';
                } else {
                    timeOutText = '---';
                }
            } else {
                if (isFuture) {
                    timeInText = '-';
                    timeOutText = '-';
                } else if (dayOfWeek === 0 || holiday) {
                    timeInText = ''; // Don't show Absent for Sunday/Holiday
                    timeOutText = '';
                } else {
                    isAbsent = true; // Mark as true absent to apply red style
                }
            }

            let rowContent = {
                date: displayDate,
                dayNum: day,
                isSunday: dayOfWeek === 0,
                isHoliday: !!holiday,
                holidayName: holiday ? holiday.name : '',
                clockIn: timeInText,
                clockOut: timeOutText,
                isAbsent: isAbsent,
                hasEntry: !!entry,
                entry: entry, // Store actual entry object
                isPending: entry?.changeRequest?.isRequested && entry.changeRequest.status === 'pending'
            };

            rows.push(rowContent);
        }
        return rows;
    };

    const handleEditClick = (row) => {
        setEditingRow(row);
        let login = '10:00';
        let logout = '19:00';

        if (row.isPending && row.entry?.changeRequest) {
            login = getTimeString(row.entry.changeRequest.requestedLogin, '10:00');
            logout = getTimeString(row.entry.changeRequest.requestedLogout, '19:00');
        } else if (row.entry) {
            login = getTimeString(row.entry.loginTime, '10:00');
            logout = getTimeString(row.entry.logoutTime, '19:00');
        }

        setEditData({ loginTime: login, logoutTime: logout });
    };

    const handleViewDetails = (row) => {
        if (!row.hasEntry) {
            showToast('No attendance record for this date.', 'info');
            return;
        }
        
        const loginStr = row.entry.loginTime ? new Date(row.entry.loginTime).toLocaleString() : 'N/A';
        const logoutStr = row.entry.logoutTime ? new Date(row.entry.logoutTime).toLocaleString() : 'N/A';
        const hours = row.entry.hoursWorked ? row.entry.hoursWorked.toFixed(2) : 0;
        const isManual = row.entry.isManual ? 'Yes (Admin Override)' : 'No';

        Swal.fire({
            title: `Attendance Details`,
            html: `
                <div style="text-align: left; margin-top: 10px; font-size: 15px;">
                    <p><strong>Date:</strong> ${row.date.split('-').reverse().join('/')}</p>
                    <p><strong>Login Time:</strong> ${loginStr}</p>
                    <p><strong>Logout Time:</strong> ${logoutStr}</p>
                    <p><strong>Total Hours:</strong> ${hours} hrs</p>
                    <p><strong>Manual Update:</strong> ${isManual}</p>
                </div>
            `,
            icon: 'info',
            confirmButtonColor: '#6366f1'
        });
    };

    const handleLoginChange = (val) => {
        setEditData({ ...editData, loginTime: val });
    };

    const handleLogoutChange = (val) => {
        setEditData({ ...editData, logoutTime: val });
    };

    const validateDates = () => {
        if (!editData.loginTime || !editData.logoutTime) return true;

        const loginDateTime = new Date(`${editingRow.date}T${editData.loginTime}`);
        const logoutDateTime = new Date(`${editingRow.date}T${editData.logoutTime}`);

        if (loginDateTime >= logoutDateTime) {
            showError('Invalid Time', 'Logout time must be after login time.');
            return false;
        }

        return true;
    };

    const handleSaveUpdate = async (e) => {
        e.preventDefault();
        if (!validateDates()) return;
        setSubmitting(true);
        try {
            await updateTimeEntry({
                employeeId: employee._id,
                date: editingRow.date,
                loginTime: combineDateAndTimeStr(editingRow.date, editData.loginTime),
                logoutTime: combineDateAndTimeStr(editingRow.date, editData.logoutTime)
            });
            setEditingRow(null);
            fetchData();
            if (onUpdate) onUpdate();
            showToast('Attendance updated successfully', 'success');
        } catch (error) {
            showError('Update Failed', error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleApproveRequest = async () => {
        if (!validateDates()) return;
        setSubmitting(true);
        try {
            await handleTimeChangeRequest(editingRow.entry._id, {
                action: 'approved',
                loginTime: combineDateAndTimeStr(editingRow.date, editData.loginTime),
                logoutTime: combineDateAndTimeStr(editingRow.date, editData.logoutTime)
            });
            setEditingRow(null);
            fetchData();
            if (onUpdate) onUpdate();
            showToast('Request approved successfully', 'success');
        } catch (error) {
            showError('Approval Failed', error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeclineRequest = async () => {
        const confirmed = await showConfirm('Decline Request?', 'Are you sure you want to decline this request?', 'warning', 'Yes, Decline');
        if (!confirmed) return;

        setSubmitting(true);
        try {
            await handleTimeChangeRequest(editingRow.entry._id, { action: 'rejected' });
            setEditingRow(null);
            fetchData();
            if (onUpdate) onUpdate();
            showToast('Request declined', 'success');
        } catch (error) {
            showError('Action Failed', error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const formatTime = (isoString) => {
        if (!isoString) return '-';
        return new Date(isoString).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const rows = generateCalendarRows();

    return (
        <div className="attendance-modal-overlay">
            <div className="attendance-modal-content">
                <div className="attendance-modal-header">
                    <h2>Attendance for {employee.name}</h2>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                <div className="controls-section">
                    <div className="control-group">
                        <label>Month:</label>
                        <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))}>
                            {Array.from({ length: 12 }, (_, i) => (
                                <option key={i} value={i + 1}>{new Date(0, i).toLocaleString('default', { month: 'long' })}</option>
                            ))}
                        </select>
                    </div>
                    <div className="control-group">
                        <label>Year:</label>
                        <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
                            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                </div>

                <div className="attendance-table-container">
                    <table className="attendance-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Clock In</th>
                                <th>Clock Out</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && attendanceData.length === 0 ? (
                                <tr><td colSpan="4" style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                            ) : (
                                rows.map(row => {
                                    // Row Style
                                    let rowStyle = {};
                                    if (row.isSunday) rowStyle = { backgroundColor: '#fee2e2' }; // Light Red
                                    if (row.isHoliday) rowStyle = { backgroundColor: '#fef3c7' }; // Light Yellow

                                    return (
                                        <tr key={row.dayNum} style={rowStyle} className={row.isPending ? 'row-pending' : ''}>
                                            <td>
                                                {row.date.split('-').reverse().join('/')}
                                                {row.isPending && <div className="pending-badge">Pending Request</div>}
                                            </td>
                                            <td>
                                                {/* Main Content */}
                                                {row.isAbsent ? (
                                                    <span className="status-absent" style={{ color: 'red', fontWeight: 'bold' }}>Absent</span>
                                                ) : (
                                                    <span className="time-val">{row.clockIn}</span>
                                                )}

                                                {/* Requested Time Highlight */}
                                                {row.isPending && (
                                                    <div className="requested-time-msg" style={{ fontSize: '0.75rem', color: '#6366f1', marginTop: '4px' }}>
                                                        Req: <strong>{formatTime(row.entry.changeRequest.requestedLogin)}</strong>
                                                    </div>
                                                )}

                                                {/* Sub Label */}
                                                {row.isSunday && <div style={{ fontSize: '0.75rem', color: '#b91c1c', fontWeight: 'bold' }}>Sunday</div>}
                                                {row.isHoliday && <div style={{ fontSize: '0.75rem', color: '#b45309', fontWeight: 'bold' }}>National Holiday ({row.holidayName})</div>}
                                            </td>
                                            <td>
                                                {/* Main Content */}
                                                {row.isAbsent ? (
                                                    <span className="status-absent" style={{ color: 'red', fontWeight: 'bold' }}>Absent</span>
                                                ) : (
                                                    <span className="time-val">{row.clockOut}</span>
                                                )}

                                                {/* Requested Time Highlight */}
                                                {row.isPending && (
                                                    <div className="requested-time-msg" style={{ fontSize: '0.75rem', color: '#6366f1', marginTop: '4px' }}>
                                                        Req: <strong>{formatTime(row.entry.changeRequest.requestedLogout)}</strong>
                                                    </div>
                                                )}

                                                {/* Sub Label */}
                                                {row.isSunday && <div style={{ fontSize: '0.75rem', color: '#b91c1c', fontWeight: 'bold' }}>Sunday</div>}
                                                {row.isHoliday && <div style={{ fontSize: '0.75rem', color: '#b45309', fontWeight: 'bold' }}>National Holiday</div>}
                                            </td>
                                            <td>
                                                <button className="btn-edit-attendance" onClick={() => handleEditClick(row)}>
                                                    {row.isPending ? 'Review' : 'Change'}
                                                </button>
                                                <button className="btn-view-details" onClick={() => handleViewDetails(row)} style={{ marginLeft: '8px', padding: '6px 12px', fontSize: '0.85rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="modal-footer">
                    <button className="btn-close" onClick={onClose}>Close</button>
                </div>

                {/* Internal Edit Modal */}
                {editingRow && (
                    <div className="edit-sub-modal">
                        <div className="edit-sub-content">
                            <h3>{editingRow.isPending ? 'Review Time Request' : 'Manual Time Update'}</h3>
                            <p>Date: <strong>{editingRow.date.split('-').reverse().join('/')}</strong></p>

                            {/* Original Times Summary */}
                            <div className="original-times-summary" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', padding: '0.75rem', backgroundColor: '#f3f4f6', borderRadius: '6px', marginBottom: '1rem' }}>
                              <div>
                                <span style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 'bold' }}>Original In</span>
                                <span style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1f2937' }}>{editingRow.entry ? formatTime(editingRow.entry.changeRequest?.originalLogin || editingRow.entry.loginTime) : '—'}</span>
                              </div>
                              <div>
                                <span style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 'bold' }}>Original Out</span>
                                <span style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1f2937' }}>{editingRow.entry ? formatTime(editingRow.entry.changeRequest?.originalLogout || editingRow.entry.logoutTime) : '—'}</span>
                              </div>
                            </div>

                            {editingRow.isPending && (
                                <div className="request-details-box" style={{ padding: '0.75rem', borderLeft: '4px solid #6366f1', backgroundColor: '#f0f2fe', borderRadius: '4px', marginBottom: '1rem' }}>
                                    <p style={{ margin: '0 0 0.5rem 0' }}><strong>Employee Reason:</strong> {editingRow.entry.changeRequest.reason}</p>
                                    <p style={{ margin: 0 }}><strong>Requested:</strong> In: {formatTime(editingRow.entry.changeRequest.requestedLogin)} | Out: {formatTime(editingRow.entry.changeRequest.requestedLogout)}</p>
                                </div>
                            )}

                            <form onSubmit={handleSaveUpdate}>
                                <div className="form-group-sub">
                                    <label>Login Time:</label>
                                    <input
                                        type="time"
                                        value={editData.loginTime}
                                        onChange={(e) => handleLoginChange(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-group-sub">
                                    <label>Logout Time:</label>
                                    <input
                                        type="time"
                                        value={editData.logoutTime}
                                        onChange={(e) => handleLogoutChange(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="sub-modal-actions">
                                    {editingRow.isPending ? (
                                        <>
                                            <button type="button" className="btn-approve-sub" onClick={handleApproveRequest} disabled={submitting}>
                                                Approve
                                            </button>
                                            <button type="button" className="btn-decline-sub" onClick={handleDeclineRequest} disabled={submitting}>
                                                Decline
                                            </button>
                                        </>
                                    ) : (
                                        <button type="submit" className="btn-save-sub" disabled={submitting}>
                                            Save Changes
                                        </button>
                                    )}
                                    <button type="button" className="btn-cancel-sub" onClick={() => setEditingRow(null)}>
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AttendanceModal;
